import React, { useCallback, useRef, useState } from 'react';

import type { ReelOutline } from './types';

type DragState = {
  /** 'chapter' or 'beat' */
  type: 'chapter' | 'beat';
  /** For chapters: chapterIndex. For beats: `chapterIndex.beatIndex` */
  key: string;
};

type UseReelBeatReorderResult = {
  // Chapter drag
  chapterDragKey: string | null;
  chapterDragOverKey: string | null;
  handleChapterDragStart: (e: React.DragEvent, chapterIdx: number) => void;
  handleChapterDragOver: (e: React.DragEvent, chapterIdx: number) => void;
  handleChapterDragLeave: () => void;
  handleChapterDrop: (e: React.DragEvent, chapterIdx: number) => void;
  handleChapterDragEnd: () => void;

  // Beat drag (within chapter)
  beatDragKey: string | null;
  beatDragOverKey: string | null;
  handleBeatDragStart: (e: React.DragEvent, chapterIdx: number, beatIdx: number) => void;
  handleBeatDragOver: (e: React.DragEvent, chapterIdx: number, beatIdx: number) => void;
  handleBeatDragLeave: () => void;
  handleBeatDrop: (e: React.DragEvent, chapterIdx: number, beatIdx: number) => void;
  handleBeatDragEnd: () => void;
};

/**
 * Drag-and-drop reordering hook for reel chapters and beats.
 * Chapters can be reordered among chapters.
 * Beats can be reordered within their chapter.
 */
export function useReelBeatReorder(
  outline: ReelOutline,
  onOutlineChange: (outline: ReelOutline) => void,
): UseReelBeatReorderResult {
  const [chapterDragKey, setChapterDragKey] = useState<string | null>(null);
  const [chapterDragOverKey, setChapterDragOverKey] = useState<string | null>(null);
  const [beatDragKey, setBeatDragKey] = useState<string | null>(null);
  const [beatDragOverKey, setBeatDragOverKey] = useState<string | null>(null);

  const dragSourceRef = useRef<DragState | null>(null);

  // --- Chapter drag handlers ---

  const handleChapterDragStart = useCallback(
    (e: React.DragEvent, chapterIdx: number) => {
      e.stopPropagation();
      const key = String(chapterIdx);
      dragSourceRef.current = { type: 'chapter', key };
      setChapterDragKey(key);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `chapter:${key}`);
    },
    [],
  );

  const handleChapterDragOver = useCallback(
    (e: React.DragEvent, chapterIdx: number) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setChapterDragOverKey(String(chapterIdx));
    },
    [],
  );

  const handleChapterDragLeave = useCallback(() => {
    setChapterDragOverKey(null);
  }, []);

  const handleChapterDrop = useCallback(
    (e: React.DragEvent, dropChapterIdx: number) => {
      e.preventDefault();
      e.stopPropagation();

      const source = dragSourceRef.current;
      setChapterDragKey(null);
      setChapterDragOverKey(null);
      dragSourceRef.current = null;

      if (!source || source.type !== 'chapter') return;

      const sourceIdx = Number(source.key);
      if (sourceIdx === dropChapterIdx) return;

      const newChapters = [...outline.chapters];
      const [moved] = newChapters.splice(sourceIdx, 1);
      newChapters.splice(dropChapterIdx, 0, moved);

      // Renumber chapter order values
      const renumbered = newChapters.map((ch, idx) => ({
        ...ch,
        order: idx + 1,
      }));

      onOutlineChange({ ...outline, chapters: renumbered });
    },
    [outline, onOutlineChange],
  );

  const handleChapterDragEnd = useCallback(() => {
    setChapterDragKey(null);
    setChapterDragOverKey(null);
    dragSourceRef.current = null;
  }, []);

  // --- Beat drag handlers (within chapter) ---

  const handleBeatDragStart = useCallback(
    (e: React.DragEvent, chapterIdx: number, beatIdx: number) => {
      e.stopPropagation();
      const key = `${chapterIdx}.${beatIdx}`;
      dragSourceRef.current = { type: 'beat', key };
      setBeatDragKey(key);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `beat:${key}`);
    },
    [],
  );

  const handleBeatDragOver = useCallback(
    (e: React.DragEvent, chapterIdx: number, beatIdx: number) => {
      // Let chapter drags bubble up to the chapter container
      if (dragSourceRef.current?.type === 'chapter') return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setBeatDragOverKey(`${chapterIdx}.${beatIdx}`);
    },
    [],
  );

  const handleBeatDragLeave = useCallback(() => {
    setBeatDragOverKey(null);
  }, []);

  const handleBeatDrop = useCallback(
    (e: React.DragEvent, dropChapterIdx: number, dropBeatIdx: number) => {
      const source = dragSourceRef.current;

      // Let chapter drags bubble up to the chapter container
      if (!source || source.type !== 'beat') return;

      e.preventDefault();
      e.stopPropagation();

      setBeatDragKey(null);
      setBeatDragOverKey(null);
      dragSourceRef.current = null;

      const [srcChapterIdx, srcBeatIdx] = source.key.split('.').map(Number);

      // Only allow reordering within the same chapter
      if (srcChapterIdx !== dropChapterIdx) return;
      if (srcBeatIdx === dropBeatIdx) return;

      const chapter = outline.chapters[srcChapterIdx];
      if (!chapter) return;

      const newBeats = [...chapter.beats];
      const [moved] = newBeats.splice(srcBeatIdx, 1);
      newBeats.splice(dropBeatIdx, 0, moved);

      // Renumber beat order values
      const renumbered = newBeats.map((beat, idx) => ({
        ...beat,
        order: idx + 1,
      }));

      const newChapters = outline.chapters.map((ch, idx) =>
        idx === srcChapterIdx ? { ...ch, beats: renumbered } : ch,
      );

      onOutlineChange({ ...outline, chapters: newChapters });
    },
    [outline, onOutlineChange],
  );

  const handleBeatDragEnd = useCallback(() => {
    setBeatDragKey(null);
    setBeatDragOverKey(null);
    dragSourceRef.current = null;
  }, []);

  return {
    chapterDragKey,
    chapterDragOverKey,
    handleChapterDragStart,
    handleChapterDragOver,
    handleChapterDragLeave,
    handleChapterDrop,
    handleChapterDragEnd,

    beatDragKey,
    beatDragOverKey,
    handleBeatDragStart,
    handleBeatDragOver,
    handleBeatDragLeave,
    handleBeatDrop,
    handleBeatDragEnd,
  };
}
